import type { IntakeTemplateContent } from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// CAR-53 — intake (anamnes) template editor. Like every other clinician
// request (see clinician-api.ts) auth rides on the httpOnly session cookie:
// `withCredentials` attaches it, `tenantHeader()` scopes the tenant. No JWT.

function commonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...tenantHeader(),
  };
}

export interface IntakeTemplateBundle {
  published: IntakeTemplateContent;
  draft: IntakeTemplateContent | null;
}

/**
 * The backend's draft guardrail (400) when a pinned field is removed/re-keyed/
 * re-typed comes back as JSON. We surface its message verbatim so the clinician
 * sees the actual rule that tripped.
 */
export class IntakeTemplateError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    // Re-use the shared 401 path so the route can sign the clinician out.
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new IntakeTemplateError(detail);
  }
  return (await res.json()) as T;
}

// CAR-92: the editor serves any editable template kind. `key` selects it
// (defaults to "intake" so existing call sites are unchanged); the backend
// gates `:key` to registered kinds (intake, weekly_checkin).
export async function fetchIntakeTemplate(
  key = "intake",
): Promise<IntakeTemplateBundle> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/forms/${key}/template`,
    {
      headers: commonHeaders(),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function saveIntakeDraft(
  content: IntakeTemplateContent,
  key = "intake",
): Promise<IntakeTemplateContent> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/forms/${key}/template/draft`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(content),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function publishIntakeTemplate(
  key = "intake",
): Promise<IntakeTemplateContent> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/forms/${key}/template/publish`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}
